// =============================================
// Admin Calendar — לוח שנה ארצי
// ניהול חגים, חופשות וימים מיוחדים
// =============================================

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CalendarDays, Plus, X, ChevronLeft, ChevronRight,
  Edit3, Trash2, Clock, CheckCircle2, XCircle,
  AlertTriangle, Save,
} from 'lucide-react';
import { adminApi } from '../../services/api';
import type { SystemCalendarDto, UpsertSystemCalendarRequest } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import AdminKindergartenCalendar from './AdminKindergartenCalendar';
import FormLabel from '../../components/common/FormLabel';
import { adminCalendarHelpTexts } from '../../constants/helpTexts';
import { calendarColors as colors, } from '../../styles/colors';
import {
  HEBREW_DAYS,
  HEBREW_MONTHS,
  formatDate,
  parseDate,
  daysInMonth,
  firstDayOfMonth,
} from '../../utils/hebrewCalendar';

// =============================================
// Constants
// =============================================

// All event types including deprecated (for display of existing entries)
const ALL_EVENT_TYPES = [
  { value: 1, label: 'חג', color: '#ef4444', bgColor: '#fef2f2' },
  { value: 2, label: 'חול המועד', color: '#f59e0b', bgColor: '#fef3c7' },
  { value: 3, label: 'חופשה', color: '#3b82f6', bgColor: '#dbeafe' },
  { value: 4, label: 'מלחמה', color: '#6b7280', bgColor: '#f3f4f6' },  // deprecated
  { value: 5, label: 'אחר', color: '#8b5cf6', bgColor: '#f5f3ff' },
];

// Selectable event types for creation (excludes deprecated)
const EVENT_TYPES = ALL_EVENT_TYPES.filter(t => t.value !== 4);

const OPERATION_MODES = [
  { value: 0, label: 'סגור', color: '#ef4444' },
  { value: 1, label: 'רגיל', color: '#22c55e' },
  { value: 2, label: 'חלקי', color: '#f59e0b' },
];

// =============================================
// Helpers
// =============================================

interface FormState {
  eventType: number;
  eventName: string;
  operationMode: number;
  startTime: string;
  endTime: string;
  notes: string;
}

const emptyForm: FormState = {
  eventType: 1,
  eventName: '',
  operationMode: 0,
  startTime: '',
  endTime: '',
  notes: '',
};

function getEventType(value: number) {
  return ALL_EVENT_TYPES.find(t => t.value === value) || ALL_EVENT_TYPES[0];
}

function getOperationMode(value: number) {
  return OPERATION_MODES.find(m => m.value === value) || OPERATION_MODES[0];
}

function formatDisplayDate(dateStr: string): string {
  const { day, month } = parseDate(dateStr);
  return `${day} ${HEBREW_MONTHS[month]}`;
}

function isToday(year: number, month: number, day: number): boolean {
  const now = new Date();
  return now.getFullYear() === year && now.getMonth() === month && now.getDate() === day;
}

function isSaturday(year: number, month: number, day: number): boolean {
  return new Date(year, month, day).getDay() === 6;
}

/** Generate all dates in a range (inclusive) */
function getDateRange(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (start > end) return dates;
  const current = new Date(start);
  while (current <= end) {
    const y = current.getFullYear();
    const m = String(current.getMonth() + 1).padStart(2, '0');
    const d = String(current.getDate()).padStart(2, '0');
    dates.push(`${y}-${m}-${d}`);
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

// =============================================
// Component
// =============================================

export default function AdminCalendar() {
  const { user } = useAuthStore();
  const isSysAdmin = !user?.organizationPublicId;
  const primaryColor = isSysAdmin ? '#7c3aed' : '#6366f1';
  const primaryLight = isSysAdmin ? '#f5f3ff' : '#eef2ff';

  // Tab view: system calendar vs KG overrides
  const [activeTab, setActiveTab] = useState<'system' | 'kindergarten'>('system');

  // ALL hooks must be declared before the early return (React rules of hooks)
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [entries, setEntries] = useState<SystemCalendarDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<SystemCalendarDto | null>(null);
  const [isRangeMode, setIsRangeMode] = useState(false);
  const [rangeEndDate, setRangeEndDate] = useState('');
  const [form, setForm] = useState<FormState>({ ...emptyForm });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  // Load entries when year changes
  useEffect(() => {
    if (activeTab !== 'system') return; // skip API call when not on system tab
    const loadEntries = async () => {
      setLoading(true);
      try {
        const result = await adminApi.getSystemCalendar(year);
        if (result.success && result.data) {
          setEntries(result.data);
        } else {
          setEntries([]);
        }
      } catch (err) {
        console.error('Failed to load calendar entries:', err);
        setErrorMessage('שגיאה בטעינת לוח שנה');
      } finally {
        setLoading(false);
      }
    };
    loadEntries();
  }, [year, activeTab]);

  // If in KG overrides tab, render the separate component
  if (activeTab === 'kindergarten') {
    return (
      <div dir="rtl" style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        {/* Tab Switcher */}
        <div style={{ display: 'flex', gap: '0', marginBottom: '24px', borderBottom: `2px solid ${colors.border}` }}>
          <button
            onClick={() => setActiveTab('system')}
            style={{
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: '600',
              border: 'none',
              borderBottom: '2px solid transparent',
              background: 'transparent',
              color: colors.textLight,
              cursor: 'pointer',
              marginBottom: '-2px',
            }}
          >
            לוח שנה ארצי
          </button>
          <button
            onClick={() => setActiveTab('kindergarten')}
            style={{
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: '600',
              border: 'none',
              borderBottom: `2px solid ${primaryColor}`,
              background: 'transparent',
              color: primaryColor,
              cursor: 'pointer',
              marginBottom: '-2px',
            }}
          >
            דריסות לגנים
          </button>
        </div>
        <AdminKindergartenCalendar primaryColor={primaryColor} primaryLight={primaryLight} />
      </div>
    );
  }

  // Toasts
  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const showError = (msg: string) => {
    setErrorMessage(msg);
    setTimeout(() => setErrorMessage(''), 4000);
  };

  // Navigation
  const goToPreviousMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear(y => y - 1);
    } else {
      setMonth(m => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(y => y + 1);
    } else {
      setMonth(m => m + 1);
    }
  };

  const goToToday = () => {
    setYear(now.getFullYear());
    setMonth(now.getMonth());
  };

  // Entries for a specific date
  const getEntriesForDate = (dateStr: string): SystemCalendarDto[] => {
    return entries.filter(e => e.date === dateStr);
  };

  // Entries for current month
  const monthEntries = entries
    .filter(e => {
      const { month: m } = parseDate(e.date);
      return m === month;
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  // Open modal for adding
  const openAddModal = (dateStr: string) => {
    setEditingEntry(null);
    setSelectedDate(dateStr);
    setIsRangeMode(false);
    setRangeEndDate('');
    setForm({ ...emptyForm });
    setConfirmDelete(null);
    setShowModal(true);
  };

  // Open modal for editing
  const openEditModal = (entry: SystemCalendarDto) => {
    setEditingEntry(entry);
    setSelectedDate(entry.date);
    setIsRangeMode(false);
    setRangeEndDate('');
    setForm({
      eventType: entry.eventType,
      eventName: entry.eventName,
      operationMode: entry.operationMode,
      startTime: entry.startTime || '',
      endTime: entry.endTime || '',
      notes: entry.notes || '',
    });
    setConfirmDelete(null);
    setShowModal(true);
  };

  // Handle day click
  const handleDayClick = (day: number) => {
    const dateStr = formatDate(year, month, day);
    const dayEntries = getEntriesForDate(dateStr);
    if (dayEntries.length === 1) {
      openEditModal(dayEntries[0]);
    } else {
      openAddModal(dateStr);
    }
  };

  // Save handler
  const handleSave = async () => {
    if (!form.eventName.trim()) {
      showError('נא להזין שם אירוע');
      return;
    }
    if (!selectedDate) return;

    setSaving(true);
    try {
      const baseRequest: UpsertSystemCalendarRequest = {
        date: selectedDate,
        eventType: form.eventType,
        eventName: form.eventName.trim(),
        operationMode: form.operationMode,
        startTime: form.operationMode === 2 ? form.startTime || undefined : undefined,
        endTime: form.operationMode === 2 ? form.endTime || undefined : undefined,
        notes: form.notes.trim() || undefined,
      };

      if (isRangeMode && rangeEndDate && !editingEntry) {
        // Bulk create for date range
        const dates = getDateRange(selectedDate, rangeEndDate);
        if (dates.length === 0) {
          showError('טווח תאריכים לא תקין');
          setSaving(false);
          return;
        }
        if (dates.length > 60) {
          showError('לא ניתן ליצור יותר מ-60 רשומות בבת אחת');
          setSaving(false);
          return;
        }
        const bulkEntries: UpsertSystemCalendarRequest[] = dates.map(d => ({
          ...baseRequest,
          date: d,
        }));
        const result = await adminApi.bulkUpsertSystemCalendar(bulkEntries);
        if (result.success && result.data) {
          // Merge new entries into state
          setEntries(prev => {
            const newIds = new Set(result.data.map((e: SystemCalendarDto) => e.id));
            const filtered = prev.filter(e => !newIds.has(e.id));
            return [...filtered, ...result.data];
          });
          showSuccess(`${dates.length} רשומות נוצרו בהצלחה`);
        } else {
          showError('שגיאה בשמירה');
        }
      } else {
        // Single upsert
        if (editingEntry) {
          baseRequest.date = editingEntry.date;
        }
        const result = await adminApi.upsertSystemCalendarEntry(baseRequest);
        if (result.success && result.data) {
          setEntries(prev => {
            if (editingEntry) {
              return prev.map(e => e.id === editingEntry.id ? result.data : e);
            }
            return [...prev, result.data];
          });
          showSuccess(editingEntry ? 'רשומה עודכנה בהצלחה' : 'רשומה נוספה בהצלחה');
        } else {
          showError('שגיאה בשמירה');
        }
      }

      setShowModal(false);
    } catch (err) {
      console.error('Failed to save calendar entry:', err);
      showError('שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  // Delete handler
  const handleDelete = async (id: number) => {
    setSaving(true);
    try {
      const result = await adminApi.deleteSystemCalendarEntry(id);
      if (result.success) {
        setEntries(prev => prev.filter(e => e.id !== id));
        showSuccess('רשומה נמחקה בהצלחה');
        setShowModal(false);
      } else {
        showError('שגיאה במחיקה');
      }
    } catch (err) {
      console.error('Failed to delete calendar entry:', err);
      showError('שגיאה במחיקה');
    } finally {
      setSaving(false);
      setConfirmDelete(null);
    }
  };

  // Calendar grid data
  const totalDays = daysInMonth(year, month);
  const startDay = firstDayOfMonth(year, month);

  return (
    <div style={{ maxWidth: '960px', margin: '0 auto' }}>
      {/* Tab Switcher */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '20px', borderBottom: `2px solid ${colors.border}` }}>
        <button
          onClick={() => setActiveTab('system')}
          style={{
            padding: '10px 24px',
            fontSize: '14px',
            fontWeight: '600',
            border: 'none',
            borderBottom: `2px solid ${primaryColor}`,
            background: 'transparent',
            color: primaryColor,
            cursor: 'pointer',
            marginBottom: '-2px',
          }}
        >
          לוח שנה ארצי
        </button>
        <button
          onClick={() => setActiveTab('kindergarten')}
          style={{
            padding: '10px 24px',
            fontSize: '14px',
            fontWeight: '600',
            border: 'none',
            borderBottom: '2px solid transparent',
            background: 'transparent',
            color: colors.textLight,
            cursor: 'pointer',
            marginBottom: '-2px',
          }}
        >
          דריסות לגנים
        </button>
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: colors.text, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CalendarDays size={24} color={primaryColor} />
            לוח שנה ארצי
          </h1>
          <p style={{ fontSize: '13px', color: colors.textLight, margin: '4px 0 0 0' }}>
            ניהול חגים, חופשות וימים מיוחדים
            {' '}&middot; {entries.length} רשומות ב-{year}
          </p>
        </div>
        <button
          onClick={() => {
            const todayStr = formatDate(now.getFullYear(), now.getMonth(), now.getDate());
            openAddModal(todayStr);
          }}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '10px 18px', backgroundColor: primaryColor, color: 'white',
            border: 'none', borderRadius: '10px', cursor: 'pointer',
            fontSize: '13px', fontWeight: '600',
          }}
        >
          <Plus size={16} /> הוסף רשומה
        </button>
      </div>

      {/* Success / Error toasts */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '12px 16px', backgroundColor: '#dcfce7',
              borderRadius: '10px', marginBottom: '16px', fontSize: '13px', color: '#166534',
            }}
          >
            <CheckCircle2 size={16} /> {successMessage}
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {errorMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '12px 16px', backgroundColor: '#fef2f2',
              borderRadius: '10px', marginBottom: '16px', fontSize: '13px', color: '#991b1b',
            }}
          >
            <XCircle size={16} /> {errorMessage}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Calendar card */}
      <div style={{
        backgroundColor: colors.white, borderRadius: '16px',
        border: `1px solid ${colors.border}`, padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '24px',
      }}>
        {/* Month/Year navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <button
            onClick={goToNextMonth}
            style={{
              padding: '8px', border: `1px solid ${colors.border}`, borderRadius: '8px',
              backgroundColor: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center',
            }}
            title="חודש הבא"
          >
            <ChevronRight size={18} color={colors.text} />
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={goToToday}
              style={{
                padding: '6px 12px', border: `1px solid ${primaryColor}`, borderRadius: '6px',
                backgroundColor: primaryLight, cursor: 'pointer', fontSize: '12px',
                fontWeight: '600', color: primaryColor,
              }}
            >
              היום
            </button>
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: colors.text, margin: 0 }}>
              {HEBREW_MONTHS[month]} {year}
            </h2>
          </div>

          <button
            onClick={goToPreviousMonth}
            style={{
              padding: '8px', border: `1px solid ${colors.border}`, borderRadius: '8px',
              backgroundColor: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center',
            }}
            title="חודש קודם"
          >
            <ChevronLeft size={18} color={colors.text} />
          </button>
        </div>

        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '4px' }}>
          {HEBREW_DAYS.map((dayName, i) => (
            <div
              key={dayName}
              style={{
                textAlign: 'center', padding: '8px 0', fontSize: '12px',
                fontWeight: '700', color: i === 6 ? '#ca8a04' : colors.textLight,
              }}
            >
              {dayName}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: colors.textLight }}>
            <div style={{
              width: '24px', height: '24px', border: `3px solid ${colors.border}`,
              borderTopColor: primaryColor, borderRadius: '50%',
              margin: '0 auto 8px', animation: 'spin 1s linear infinite',
            }} />
            <p style={{ fontSize: '13px' }}>טוען לוח שנה...</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
            {/* Empty cells before first day */}
            {Array.from({ length: startDay }).map((_, i) => (
              <div key={`empty-${i}`} style={{ minHeight: '80px' }} />
            ))}

            {/* Day cells */}
            {Array.from({ length: totalDays }).map((_, i) => {
              const day = i + 1;
              const dateStr = formatDate(year, month, day);
              const dayEntries = getEntriesForDate(dateStr);
              const today = isToday(year, month, day);
              const shabbat = isSaturday(year, month, day);

              return (
                <motion.div
                  key={day}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleDayClick(day)}
                  style={{
                    minHeight: '80px',
                    padding: '6px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: today ? `2px solid ${primaryColor}` : `1px solid ${colors.border}`,
                    backgroundColor: today
                      ? '#ccfbf1'
                      : shabbat
                        ? '#fefce8'
                        : colors.white,
                    transition: 'background-color 0.15s',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Day number */}
                  <div style={{
                    fontSize: '13px', fontWeight: today ? '700' : '500',
                    color: today ? primaryColor : shabbat ? '#ca8a04' : colors.text,
                    marginBottom: '4px',
                  }}>
                    {day}
                  </div>

                  {/* Event indicators */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {dayEntries.slice(0, 2).map(entry => {
                      const evType = getEventType(entry.eventType);
                      return (
                        <div
                          key={entry.id}
                          style={{
                            fontSize: '9px',
                            fontWeight: '600',
                            padding: '1px 4px',
                            borderRadius: '3px',
                            backgroundColor: evType.bgColor,
                            color: evType.color,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: '14px',
                          }}
                        >
                          {entry.eventName}
                        </div>
                      );
                    })}
                    {dayEntries.length > 2 && (
                      <div style={{ fontSize: '9px', color: colors.textLight, fontWeight: '500' }}>
                        +{dayEntries.length - 2} נוספים
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Events list for current month */}
      <div style={{
        backgroundColor: colors.white, borderRadius: '16px',
        border: `1px solid ${colors.border}`, padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}>
        <h3 style={{
          fontSize: '16px', fontWeight: '700', color: colors.text,
          margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <CalendarDays size={18} color={primaryColor} />
          אירועים ב-{HEBREW_MONTHS[month]} {year}
          <span style={{ fontSize: '13px', fontWeight: '500', color: colors.textLight }}>
            ({monthEntries.length})
          </span>
        </h3>

        {monthEntries.length === 0 && !loading ? (
          <div style={{
            textAlign: 'center', padding: '40px 24px',
            backgroundColor: colors.background, borderRadius: '12px',
          }}>
            <CalendarDays size={36} color={colors.textLight} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
            <p style={{ fontSize: '14px', fontWeight: '600', color: colors.text, margin: '0 0 4px 0' }}>
              אין אירועים בחודש זה
            </p>
            <p style={{ fontSize: '13px', color: colors.textLight, margin: 0 }}>
              לחץ על יום בלוח כדי להוסיף אירוע
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {monthEntries.map((entry, index) => {
              const evType = getEventType(entry.eventType);
              const opMode = getOperationMode(entry.operationMode);
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 16px', borderRadius: '10px',
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.white,
                  }}
                >
                  {/* Date */}
                  <div style={{
                    minWidth: '60px', textAlign: 'center',
                    padding: '6px 8px', borderRadius: '8px',
                    backgroundColor: colors.background,
                  }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: colors.text }}>
                      {parseDate(entry.date).day}
                    </div>
                    <div style={{ fontSize: '10px', color: colors.textLight, fontWeight: '500' }}>
                      {HEBREW_MONTHS[parseDate(entry.date).month].slice(0, 3)}
                    </div>
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>
                        {entry.eventName}
                      </span>
                      {/* Event type badge */}
                      <span style={{
                        fontSize: '10px', fontWeight: '600', padding: '2px 8px',
                        borderRadius: '10px', backgroundColor: evType.bgColor, color: evType.color,
                      }}>
                        {evType.label}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: colors.textLight, flexWrap: 'wrap' }}>
                      {/* Operation mode */}
                      <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: opMode.color, fontWeight: '500' }}>
                        {opMode.value === 0 && <XCircle size={12} />}
                        {opMode.value === 1 && <CheckCircle2 size={12} />}
                        {opMode.value === 2 && <Clock size={12} />}
                        {opMode.label}
                      </span>
                      {/* Time range for partial */}
                      {entry.operationMode === 2 && entry.startTime && entry.endTime && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <Clock size={11} />
                          {entry.startTime} - {entry.endTime}
                        </span>
                      )}
                      {/* Notes preview */}
                      {entry.notes && (
                        <span style={{
                          maxWidth: '200px', overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {entry.notes}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Edit button */}
                  <button
                    onClick={() => openEditModal(entry)}
                    style={{
                      padding: '8px', border: `1px solid ${colors.border}`, borderRadius: '8px',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      display: 'flex', alignItems: 'center',
                    }}
                    title="עריכה"
                  >
                    <Edit3 size={14} color={colors.textLight} />
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: '16px',
            }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              style={{
                backgroundColor: colors.white, borderRadius: '16px',
                width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto',
                padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              }}
              onMouseDown={e => e.stopPropagation()}
            >
              {/* Modal header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: colors.text, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalendarDays size={18} color={primaryColor} />
                  {editingEntry ? 'עריכת רשומה' : 'הוספת רשומה'}
                </h3>
                <button onClick={() => setShowModal(false)} style={{ padding: '4px', border: 'none', background: 'none', cursor: 'pointer' }}>
                  <X size={18} color={colors.textLight} />
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Date picker */}
                <div>
                  <FormLabel text="מתאריך" required helpText={adminCalendarHelpTexts.startDate.tooltip} style={{ color: colors.text }} />
                  {isRangeMode ? (
                    <div style={{
                      padding: '10px 12px', backgroundColor: colors.background,
                      borderRadius: '8px', fontSize: '13px', color: colors.text,
                      border: `1px solid ${colors.border}`,
                    }}>
                      {selectedDate && formatDisplayDate(selectedDate)}
                      {rangeEndDate && (
                        <span> &mdash; {formatDisplayDate(rangeEndDate)}</span>
                      )}
                    </div>
                  ) : (
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={e => setSelectedDate(e.target.value)}
                      style={{
                        width: '100%', padding: '10px 12px',
                        borderRadius: '8px', fontSize: '13px', color: colors.text,
                        border: `1px solid ${colors.border}`, outline: 'none',
                        boxSizing: 'border-box', backgroundColor: colors.white,
                      }}
                    />
                  )}
                </div>

                {/* Range mode toggle (only for new entries) */}
                {!editingEntry && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: colors.text }}>
                      יצירה בטווח תאריכים
                    </label>
                    <button
                      onClick={() => setIsRangeMode(!isRangeMode)}
                      style={{
                        width: '44px', height: '24px', borderRadius: '12px',
                        border: 'none', cursor: 'pointer', position: 'relative',
                        backgroundColor: isRangeMode ? primaryColor : colors.border,
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <div style={{
                        width: '18px', height: '18px', borderRadius: '50%',
                        backgroundColor: colors.white, position: 'absolute',
                        top: '3px',
                        left: isRangeMode ? '3px' : '23px',
                        transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </button>
                  </div>
                )}

                {/* Range end date picker */}
                {isRangeMode && !editingEntry && (
                  <div>
                    <FormLabel text="עד תאריך" required helpText={adminCalendarHelpTexts.endDate.tooltip} style={{ color: colors.text }} />
                    <input
                      type="date"
                      value={rangeEndDate}
                      min={selectedDate || undefined}
                      onChange={e => setRangeEndDate(e.target.value)}
                      style={{
                        width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`,
                        borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                      }}
                    />
                  </div>
                )}

                {/* Event type select */}
                <div>
                  <FormLabel text="סוג אירוע" required helpText={adminCalendarHelpTexts.eventType.tooltip} style={{ color: colors.text }} />
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {EVENT_TYPES.map(et => (
                      <button
                        key={et.value}
                        onClick={() => setForm(f => ({ ...f, eventType: et.value }))}
                        style={{
                          padding: '8px 14px', borderRadius: '8px', cursor: 'pointer',
                          fontSize: '12px', fontWeight: '600',
                          border: form.eventType === et.value ? `2px solid ${et.color}` : `1px solid ${colors.border}`,
                          backgroundColor: form.eventType === et.value ? et.bgColor : colors.white,
                          color: form.eventType === et.value ? et.color : colors.textLight,
                          transition: 'all 0.15s',
                        }}
                      >
                        {et.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Event name */}
                <div>
                  <FormLabel text="שם האירוע" required helpText={adminCalendarHelpTexts.eventName.tooltip} style={{ color: colors.text }} />
                  <input
                    type="text"
                    value={form.eventName}
                    onChange={e => setForm(f => ({ ...f, eventName: e.target.value }))}
                    placeholder="לדוגמה: פסח, חופשת קיץ..."
                    style={{
                      width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`,
                      borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* Operation mode */}
                <div>
                  <FormLabel text="מצב פעילות" helpText={adminCalendarHelpTexts.operationMode.tooltip} style={{ color: colors.text }} />
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {OPERATION_MODES.map(om => (
                      <button
                        key={om.value}
                        onClick={() => setForm(f => ({ ...f, operationMode: om.value }))}
                        style={{
                          flex: 1, padding: '10px 8px', borderRadius: '8px', cursor: 'pointer',
                          fontSize: '13px', fontWeight: '600', textAlign: 'center',
                          border: form.operationMode === om.value ? `2px solid ${om.color}` : `1px solid ${colors.border}`,
                          backgroundColor: form.operationMode === om.value
                            ? om.value === 0 ? '#fef2f2' : om.value === 1 ? '#dcfce7' : '#fef3c7'
                            : colors.white,
                          color: form.operationMode === om.value ? om.color : colors.textLight,
                          transition: 'all 0.15s',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                        }}
                      >
                        {om.value === 0 && <XCircle size={14} />}
                        {om.value === 1 && <CheckCircle2 size={14} />}
                        {om.value === 2 && <Clock size={14} />}
                        {om.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time inputs (only when partial) */}
                {form.operationMode === 2 && (
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: colors.text, marginBottom: '6px' }}>
                        שעת התחלה
                      </label>
                      <input
                        type="time"
                        value={form.startTime}
                        onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))}
                        style={{
                          width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`,
                          borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                        }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: colors.text, marginBottom: '6px' }}>
                        שעת סיום
                      </label>
                      <input
                        type="time"
                        value={form.endTime}
                        onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))}
                        style={{
                          width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`,
                          borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <FormLabel text="הערות" helpText={adminCalendarHelpTexts.notes.tooltip} style={{ color: colors.text }} />
                  <textarea
                    value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="הערות נוספות (אופציונלי)"
                    rows={3}
                    style={{
                      width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`,
                      borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                      resize: 'vertical', fontFamily: 'inherit',
                    }}
                  />
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                  {/* Save */}
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      padding: '12px', border: 'none', borderRadius: '10px',
                      backgroundColor: primaryColor, color: 'white', cursor: saving ? 'not-allowed' : 'pointer',
                      fontSize: '14px', fontWeight: '600', opacity: saving ? 0.7 : 1,
                      transition: 'opacity 0.15s',
                    }}
                  >
                    {saving ? (
                      <div style={{
                        width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)',
                        borderTopColor: 'white', borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                      }} />
                    ) : (
                      <Save size={16} />
                    )}
                    {saving ? 'שומר...' : 'שמור'}
                  </button>

                  {/* Delete (only when editing) */}
                  {editingEntry && (
                    <>
                      {confirmDelete === editingEntry.id ? (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            onClick={() => handleDelete(editingEntry.id)}
                            disabled={saving}
                            style={{
                              padding: '12px 16px', border: 'none', borderRadius: '10px',
                              backgroundColor: '#ef4444', color: 'white', cursor: saving ? 'not-allowed' : 'pointer',
                              fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px',
                            }}
                          >
                            <AlertTriangle size={14} /> אישור
                          </button>
                          <button
                            onClick={() => setConfirmDelete(null)}
                            style={{
                              padding: '12px', border: `1px solid ${colors.border}`, borderRadius: '10px',
                              backgroundColor: 'transparent', cursor: 'pointer',
                              display: 'flex', alignItems: 'center',
                            }}
                          >
                            <X size={16} color={colors.textLight} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDelete(editingEntry.id)}
                          style={{
                            padding: '12px 16px', border: '1px solid #fecaca', borderRadius: '10px',
                            backgroundColor: '#fef2f2', cursor: 'pointer',
                            fontSize: '13px', fontWeight: '600', color: '#ef4444',
                            display: 'flex', alignItems: 'center', gap: '6px',
                          }}
                        >
                          <Trash2 size={14} /> מחק
                        </button>
                      )}
                    </>
                  )}

                  {/* Cancel */}
                  <button
                    onClick={() => setShowModal(false)}
                    style={{
                      padding: '12px 16px', border: `1px solid ${colors.border}`, borderRadius: '10px',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      fontSize: '13px', color: colors.textLight,
                    }}
                  >
                    ביטול
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Keyframes for spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
